import React from 'react';
import { Link } from 'react-router-dom';
import BlogLayout from '../blog/BlogLayout';
import SEO from '../components/SEO';
import { BreadcrumbStructuredData } from '../components/StructuredData';
import {
  DEFAULT_INPUTS,
  LIMITS,
  calculate,
  formatCount,
  formatMoney,
  formatRate,
  inputsFromSearch,
  searchFromInputs,
} from './retentionValue';
import type { RetentionKey } from './retentionValue';

const SITE_URL = 'https://www.greyfielddata.com';
const URL_PATH = '/tools/retention-value-calculator';

interface Field {
  key: RetentionKey;
  label: string;
  help: string;
  prefix?: string;
  suffix?: string;
  step: number;
}

const groups: { heading: string; fields: Field[] }[] = [
  {
    heading: 'Your customers today',
    fields: [
      { key: 'newCustomers', label: 'New customers a year', help: 'First-time buyers in the last 12 months.', step: 100 },
      { key: 'currentRate', label: 'Current 90-day repeat rate', help: 'Share of first-time buyers who ordered again within 90 days.', suffix: '%', step: 0.5 },
      { key: 'aov', label: 'Average order value', help: 'Repeat orders only, if you have it.', prefix: '$', step: 1 },
      { key: 'margin', label: 'Gross margin', help: 'After product cost, shipping, and payment fees.', suffix: '%', step: 1 },
    ],
  },
  {
    heading: 'The goal',
    fields: [
      { key: 'targetRate', label: 'Target 90-day repeat rate', help: 'A realistic goal for the next year.', suffix: '%', step: 0.5 },
      { key: 'repeatOrders', label: 'Repeat orders each in a year', help: 'How many more orders a repeat customer places in 12 months.', suffix: 'orders', step: 0.1 },
    ],
  },
];

type Draft = Record<RetentionKey, string>;

function toDraft(values: Record<RetentionKey, number>): Draft {
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [key, String(value)])) as Draft;
}

export default function RetentionValuePage() {
  const [draft, setDraft] = React.useState<Draft>(() => toDraft(DEFAULT_INPUTS));
  const [edited, setEdited] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const result = calculate(draft);
  const { input } = result;

  React.useEffect(() => {
    setDraft(toDraft(inputsFromSearch(window.location.search)));
  }, []);

  // Only touch the address bar after an edit, debounced, keeping other params, the hash, and router state.
  React.useEffect(() => {
    if (!edited) return;
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      new URLSearchParams(searchFromInputs(draft)).forEach((value, key) => params.set(key, value));
      window.history.replaceState(window.history.state, '', `${window.location.pathname}?${params.toString()}${window.location.hash}`);
    }, 600);
    return () => window.clearTimeout(timer);
  }, [draft, edited]);

  function update(key: RetentionKey, value: string) {
    setCopied(false);
    setEdited(true);
    setDraft((current) => ({ ...current, [key]: value }));
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(`${SITE_URL}${URL_PATH}${searchFromInputs(draft)}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <BlogLayout wide>
      <SEO
        title="Retention value calculator: what a better repeat rate is worth"
        description="Estimate the revenue and gross profit from getting more first-time buyers to order again within 90 days, and what one point of repeat rate is worth."
        url={`${SITE_URL}${URL_PATH}`}
      />
      <BreadcrumbStructuredData
        items={[
          { name: 'Home', url: SITE_URL },
          { name: 'Resources', url: `${SITE_URL}/resources` },
          { name: 'Retention value calculator', url: `${SITE_URL}${URL_PATH}` },
        ]}
      />

      <nav aria-label="Breadcrumb" className="text-sm text-slate-500">
        <Link to="/resources" className="hover:text-[#1a4e94]">Resources</Link>
        <span aria-hidden="true" className="mx-2">/</span>
        <span className="text-slate-700">Retention value calculator</span>
      </nav>

      <header className="mt-5 max-w-3xl">
        <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[#1a4e94]">Free tool · Ecommerce retention</p>
        <h1 className="mt-3 text-4xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-5xl">What is a better repeat rate worth?</h1>
        <p className="mt-5 text-lg leading-8 text-slate-600">Put a dollar figure on getting more first-time buyers to order again within 90 days. Use your own numbers, then see what one point of repeat rate is worth in revenue and gross profit.</p>
        <p className="mt-3 text-sm text-slate-500">An estimate from your inputs. Nothing you enter is sent anywhere.</p>
      </header>

      <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_400px]">
        <form className="space-y-6" onSubmit={(event) => event.preventDefault()}>
          {groups.map((group) => (
            <fieldset key={group.heading} className="rounded-3xl border border-slate-200 p-5 sm:p-7">
              <legend className="px-2 text-lg font-semibold text-slate-950">{group.heading}</legend>
              <div className="grid gap-6 sm:grid-cols-2">
                {group.fields.map((field) => {
                  const id = `rv-${field.key}`;
                  const [min, max] = LIMITS[field.key];
                  return (
                    <div key={field.key}>
                      <label htmlFor={id} className="block text-sm font-semibold text-slate-900">{field.label}</label>
                      <div className="mt-2 flex items-center rounded-xl border border-slate-300 bg-white focus-within:border-[#1a4e94] focus-within:ring-2 focus-within:ring-blue-100">
                        {field.prefix && <span className="pl-3 text-slate-500" aria-hidden="true">{field.prefix}</span>}
                        <input
                          id={id}
                          name={field.key}
                          type="number"
                          inputMode="decimal"
                          min={min}
                          max={max}
                          step={field.step}
                          value={draft[field.key]}
                          onChange={(event) => update(field.key, event.target.value)}
                          aria-describedby={`${id}-help`}
                          className="w-full min-w-0 rounded-xl bg-transparent px-3 py-2.5 text-slate-950 outline-none"
                        />
                        {field.suffix && <span className="pr-3 text-sm text-slate-500" aria-hidden="true">{field.suffix}</span>}
                      </div>
                      <p id={`${id}-help`} className="mt-1.5 text-xs leading-5 text-slate-500">{field.help}</p>
                    </div>
                  );
                })}
              </div>
            </fieldset>
          ))}
          <button type="button" onClick={() => { setCopied(false); setEdited(true); setDraft(toDraft(DEFAULT_INPUTS)); }} className="inline-flex min-h-[40px] items-center text-sm font-semibold text-[#1a4e94] underline underline-offset-4">Reset to example values</button>
        </form>

        <aside className="lg:sticky lg:top-28 lg:self-start">
          <p role="status" className="sr-only">{`${formatMoney(result.extraRevenue)} more revenue a year, ${formatMoney(result.extraProfit)} gross profit. Each point of repeat rate is worth ${formatMoney(result.revenuePerPoint)}.`}</p>
          <div className="rounded-3xl bg-[#0a1628] p-7 text-white">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#93b4e6]">Moving from {formatRate(input.currentRate)} to {formatRate(input.targetRate)}</p>
            <p className="mt-3 text-5xl font-semibold tabular-nums tracking-tight">{formatMoney(result.extraRevenue)}</p>
            <p className="mt-1 text-slate-300">more revenue a year, about <strong className="text-white">{formatMoney(result.extraProfit)}</strong> in gross profit</p>
            <dl className="mt-6 space-y-3.5 border-t border-[#1e3a66] pt-5 text-sm">
              <div className="flex justify-between gap-4"><dt className="text-slate-300">Extra repeat customers</dt><dd className="font-semibold tabular-nums">{formatCount(result.extraCustomers)}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-slate-300">Extra repeat orders</dt><dd className="font-semibold tabular-nums">{formatCount(result.extraOrders)}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-slate-300">Each point of repeat rate</dt><dd className="text-right font-semibold tabular-nums">{formatMoney(result.revenuePerPoint)} revenue<br /><span className="font-normal text-slate-300">{formatMoney(result.profitPerPoint)} gross profit</span></dd></div>
            </dl>
            {result.targetBelowCurrent && (
              <p className="mt-5 rounded-xl bg-amber-400/10 p-4 text-sm leading-6 text-amber-100">The target is below the current rate, so there is no gain to count. Raise the target to see the value.</p>
            )}
            <p className="mt-5 rounded-xl bg-[#13294a] p-4 text-sm leading-6 text-slate-300">Repeat rate here is the share of first-time buyers who order again within 90 days. Only count customers who have had the full 90 days.</p>
            <button type="button" onClick={copyLink} className="mt-5 w-full rounded-xl border border-white/20 px-4 py-2.5 text-sm font-semibold hover:bg-white/10">
              {copied ? 'Link copied' : 'Copy a link to these numbers'}
            </button>
          </div>
          <p className="mt-4 px-1 text-xs leading-5 text-slate-500">An estimate, not a forecast. It counts only the extra repeat orders in the first year, not longer-term lifetime value.</p>
        </aside>
      </div>

      <section className="mt-14 grid gap-10 border-t border-slate-200 pt-12 lg:grid-cols-2">
        <div>
          <h2 className="text-2xl font-semibold text-slate-950">How the numbers work</h2>
          <ul className="mt-5 space-y-3 leading-7 text-slate-600">
            <li><strong className="text-slate-900">Extra repeat customers</strong> = new customers × (target rate − current rate)</li>
            <li><strong className="text-slate-900">Extra repeat orders</strong> = extra repeat customers × repeat orders each in a year</li>
            <li><strong className="text-slate-900">Extra revenue</strong> = extra repeat orders × average order value</li>
            <li><strong className="text-slate-900">Gross profit</strong> = extra revenue × gross margin</li>
          </ul>
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-slate-950">Before you trust the result</h2>
          <ul className="mt-5 space-y-3 leading-7 text-slate-600">
            <li>Measure the current rate on cohorts with a full 90 days of history. Recent customers drag it down.</li>
            <li>Some customers would have come back anyway. Test a change against a holdout group before counting all of it.</li>
            <li>See how repeat rate, frequency, and reactivation are measured in a working <Link to="/blog/consumer-brand-retention-dashboard-in-sigma" className="text-[#1a4e94] underline underline-offset-4">customer retention dashboard in Sigma</Link>.</li>
          </ul>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/ecommerce" className="font-semibold text-[#1a4e94] underline underline-offset-4">Ecommerce reporting help</Link>
            <Link to="/blog/contribution-margin-ltv-by-acquisition-channel" className="font-semibold text-[#1a4e94] underline underline-offset-4">Contribution margin LTV by channel</Link>
            <Link to="/resources" className="font-semibold text-[#1a4e94] underline underline-offset-4">More resources</Link>
          </div>
        </div>
      </section>
    </BlogLayout>
  );
}
